import LayoutInside from '@/layout/LayoutInside'
import HeaderOnboard from '@/components/Onboarding/HeaderOnboard'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SellerContractService } from '@/services/seller-contract.service'
import Loader from '@/components/Loader/Loader'
import { useToast } from '@/hooks/use-toast'
import { AuthService } from '@/services/auth.service'
import { ContractPdfViewer } from './ContractPdfViewer'
// import { format, parseISO } from 'date-fns'
// import { ptBR } from 'date-fns/locale'
import { downloadBlobAsFile } from '@/utils/downloadBlobAsFile'
import { getReturnToFromContract } from '@/auth/contractRedirect'
import { useIsMobile } from '@/hooks/use-mobile'

const SUPPORTED_LOCALES = ['pt', 'en'] as const
type ContractLocale = typeof SUPPORTED_LOCALES[number]

function normalizeLocale(lang: string | undefined): ContractLocale {
  const base = (lang ?? 'pt').split('-')[0]?.toLowerCase() ?? 'pt'
  return SUPPORTED_LOCALES.includes(base as ContractLocale)
    ? (base as ContractLocale)
    : 'en'
}

// function formatAcceptedAt(iso: string | null | undefined): string | null {
//   if (!iso) return null
//   try {
//     return format(parseISO(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
//   } catch {
//     return iso
//   }
// }

export default function Contract() {
  const { t } = useTranslation(['routes', 'onboarding', 'common'])
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [agree, setAgree] = useState(false)
  const [locale, setLocale] = useState<ContractLocale>(() =>
    normalizeLocale(i18n.language),
  )
  /** PDF exibido no viewer: contrato (não assinado) ou comprovante assinado */
  const [viewerPdfBlob, setViewerPdfBlob] = useState<Blob | null>(null)
  // const [viewerLoadError, setViewerLoadError] = useState(false)
  const [isViewerLoading, setIsViewerLoading] = useState(false)
  const [iframeFallbackUrl, setIframeFallbackUrl] = useState<string | null>(
    null,
  )
  const iframeBlobUrlRef = useRef<string | null>(null)
  // const [isDownloadingSigned, setIsDownloadingSigned] = useState(false)

  const returnTo = useMemo(() => {
    return getReturnToFromContract(location)
  }, [location])

  const {
    data: current,
    isPending: isLoadingCurrent,
    isError: isCurrentError,
    error: currentError,
  } = useQuery({
    queryKey: ['seller-contract', 'current'],
    queryFn: () => SellerContractService.getCurrent(),
  })

  const { data: userInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: () => AuthService.userInfo(),
    staleTime: 1000 * 60 * 60 * 24,
  })

  const version = current?.contract_version
  const remotePdfUrl = current?.pdf_url?.trim()
  const isSigned =
    userInfo?.contract_signed === true ||
    current?.contract_signed === true ||
    current?.accepted === true

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (iframeBlobUrlRef.current) {
        URL.revokeObjectURL(iframeBlobUrlRef.current)
        iframeBlobUrlRef.current = null
      }
      setViewerPdfBlob(null)
      // setViewerLoadError(false)
      setIframeFallbackUrl(null)

      if (!version) {
        setIsViewerLoading(false)
        return
      }

      // Após assinado, não carrega mais o comprovante/PDF assinado — só redireciona.
      if (isSigned) {
        setIsViewerLoading(false)
        return
      }

      setIsViewerLoading(true)

      try {
        // if (isSigned) {
        //   const blob = await SellerContractService.reissueSignedPdfBlob(version, locale)
        //   if (cancelled) return
        //   setViewerPdfBlob(blob)
        //   return
        // }

        if (remotePdfUrl && /^https?:\/\//i.test(remotePdfUrl)) {
          const res = await fetch(remotePdfUrl, { mode: 'cors' })
          if (!res.ok) throw new Error('fetch failed')
          const blob = await res.blob()
          if (cancelled) return
          setViewerPdfBlob(blob)
          return
        }

        const blob = await SellerContractService.fetchSourceBlob(
          version,
          locale,
        )
        if (cancelled) return
        setViewerPdfBlob(blob)
      } catch {
        if (cancelled) return
        // setViewerLoadError(true)
        // if (isSigned) {
        //   toast({
        //     title: t('onboarding:contract_toast_receipt_load_title'),
        //     description: t('onboarding:contract_toast_receipt_load_desc'),
        //     variant: 'destructive',
        //   })
        //   return
        // }
        if (remotePdfUrl && /^https?:\/\//i.test(remotePdfUrl)) {
          setIframeFallbackUrl(remotePdfUrl)
        } else {
          setViewerPdfBlob(null)
          toast({
            title: t('onboarding:contract_toast_contract_load_title'),
            description: t('onboarding:contract_toast_contract_load_desc'),
            variant: 'destructive',
          })
        }
      } finally {
        if (!cancelled) {
          setIsViewerLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [version, locale, remotePdfUrl, isSigned, toast, t])

  // Já assinado: segue o fluxo sem exibir o contrato/comprovante.
  useEffect(() => {
    if (isLoadingCurrent || !userInfo) return
    if (!isSigned) return
    navigate(returnTo || '/dashboard', { replace: true })
  }, [isSigned, isLoadingCurrent, userInfo, navigate, returnTo])

  useEffect(() => {
    return () => {
      if (iframeBlobUrlRef.current) {
        URL.revokeObjectURL(iframeBlobUrlRef.current)
        iframeBlobUrlRef.current = null
      }
    }
  }, [])

  const acceptMutation = useMutation({
    mutationFn: () =>
      SellerContractService.accept({
        contract_version: version,
        locale,
        agree,
      }),
    onSuccess: async () => {
      queryClient.setQueryData(['user-info'], (prev: unknown) => {
        if (!prev || typeof prev !== 'object') return prev
        return {
          ...(prev as Record<string, unknown>),
          contract_signed: true,
        }
      })
      await queryClient.invalidateQueries({ queryKey: ['user-info'] })
      await queryClient.invalidateQueries({ queryKey: ['seller-contract'] })
      setAgree(false)
      toast({
        title: t('onboarding:contract_toast_accept_ok_title'),
        description: t('onboarding:contract_toast_accept_ok_desc'),
      })
      navigate(returnTo || '/dashboard', { replace: true })
    },
    onError: () => {
      toast({
        title: t('onboarding:contract_toast_accept_fail_title'),
        description: t('onboarding:contract_toast_accept_fail_desc'),
        variant: 'destructive',
      })
    },
  })

  // Download do contrato/comprovante assinado (desativado — após assinar só redireciona).
  // const downloadSignedSellerPdf = useCallback(
  //   async (kind: 'contract' | 'receipt') => {
  //     if (!version) {
  //       toast({
  //         title: t('onboarding:contract_toast_download_fail_title'),
  //         description: t('onboarding:contract_data_error'),
  //         variant: 'destructive',
  //       })
  //       return
  //     }
  //
  //     setIsDownloadingSigned(true)
  //     try {
  //       const filename =
  //         kind === 'receipt'
  //           ? `comprovante-assinatura-${version}.pdf`
  //           : `contrato-assinado-${version}.pdf`
  //
  //       const blob =
  //         kind === 'receipt'
  //           ? await SellerContractService.reissueSignedPdfBlob(version, locale)
  //           : await SellerContractService.fetchSignedDownloadBlob(version)
  //       downloadBlobAsFile(blob, filename)
  //
  //       const url = URL.createObjectURL(blob)
  //       window.open(url, '_blank', 'noopener')
  //       window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
  //     } catch {
  //       toast({
  //         title: t('onboarding:contract_toast_download_fail_title'),
  //         description: t('onboarding:contract_data_error'),
  //         variant: 'destructive',
  //       })
  //     } finally {
  //       setIsDownloadingSigned(false)
  //     }
  //   },
  //   [version, toast, t],
  // )

  const handlePdfViewerError = useCallback(() => {
    if (iframeFallbackUrl) return
    if (!viewerPdfBlob) return

    if (!isSigned) {
      const remote =
        remotePdfUrl && /^https?:\/\//i.test(remotePdfUrl) ? remotePdfUrl : null
      if (remote) {
        setIframeFallbackUrl(remote)
        return
      }
    }

    if (iframeBlobUrlRef.current) {
      URL.revokeObjectURL(iframeBlobUrlRef.current)
    }
    iframeBlobUrlRef.current = URL.createObjectURL(viewerPdfBlob)
    setIframeFallbackUrl(iframeBlobUrlRef.current)
  }, [iframeFallbackUrl, viewerPdfBlob, remotePdfUrl, isSigned])

  // const acceptedAtLabel = formatAcceptedAt(current?.accepted_at)
  // const hasSignatureDetails =
  //   current?.signature != null && Object.keys(current.signature).length > 0

  if (isLoadingCurrent || isSigned) {
    return (
      <LayoutInside
        items={[
          { text: t('dashboard'), link: '/dashboard' },
          { text: t('onboarding:seller_contract'), link: '/contract' },
        ]}
        fullWidth
      >
        <HeaderOnboard
          variant="success"
          text={t('onboarding:contract_global_update_notice')}
        />
        <div className="flex items-center justify-center min-h-[40vh]">
          <Loader />
        </div>
      </LayoutInside>
    )
  }

  if (isCurrentError) {
    return (
      <LayoutInside
        items={[
          { text: t('dashboard'), link: '/dashboard' },
          { text: t('onboarding:seller_contract'), link: '/contract' },
        ]}
        fullWidth
      >
        <HeaderOnboard
          variant="success"
          text={t('onboarding:contract_global_update_notice')}
        />
        <p className="text-sm text-destructive">
          {(currentError as Error)?.message ?? t('onboarding:contract_fetch_error')}
        </p>
      </LayoutInside>
    )
  }

  return (
    <LayoutInside
      items={[
        { text: t('dashboard'), link: '/dashboard' },
        { text: t('onboarding:seller_contract'), link: '/contract' },
      ]}
      fullWidth
    >
      <HeaderOnboard
        variant="success"
        text={t('onboarding:contract_global_update_notice')}
      />
      <div className="max-w-4xl mx-auto space-y-6 p-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-white">
            {t('onboarding:contract_title_unsigned')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('onboarding:contract_subtitle_unsigned')}
          </p>
          {/* UI de contrato já assinado (desativada — redireciona após assinar)
          {isSigned && acceptedAtLabel && (
            <p className="text-sm text-emerald-400/90">
              {t('onboarding:contract_signed_at', { date: acceptedAtLabel })}
            </p>
          )}
          */}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t('onboarding:contract_doc_language')}
          </span>
          {SUPPORTED_LOCALES.map((loc) => (
            <Button
              key={loc}
              type="button"
              variant={locale === loc ? 'default' : 'outline'}
              size="sm"
              className="h-8"
              onClick={() => setLocale(loc)}
              disabled={acceptMutation.isPending}
            >
              {t(`common:languages.${loc}`)}
            </Button>
          ))}
        </div>

        <div className="space-y-2">
          <h2 className="text-sm font-medium text-zinc-300">
            {t('onboarding:contract_section_read')}
          </h2>
          <section className="rounded-xl border border-colorBorder bg-[#18181B] overflow-hidden">
            {iframeFallbackUrl ? (
              isMobile ? (
                <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 bg-zinc-950 px-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('onboarding:contract_viewer_mobile_notice')}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() =>
                        window.open(iframeFallbackUrl, '_blank', 'noopener')
                      }
                    >
                      {t('onboarding:contract_open_pdf')}
                    </Button>
                    {viewerPdfBlob && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          downloadBlobAsFile(
                            viewerPdfBlob,
                            `contrato-${version ?? 'atual'}.pdf`,
                          )
                        }
                      >
                        {t('onboarding:download_contract')}
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <iframe
                  title={t('onboarding:contract_iframe_contract_title')}
                  src={iframeFallbackUrl}
                  className="w-full min-h-[60vh] border-0 bg-zinc-950"
                />
              )
            ) : isViewerLoading ? (
              <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 bg-zinc-950 px-4">
                <Loader />
                <p className="text-sm text-muted-foreground">
                  {t('onboarding:contract_loading_source')}
                </p>
              </div>
            ) : viewerPdfBlob ? (
              <ContractPdfViewer
                file={viewerPdfBlob}
                documentTitle={t('onboarding:contract_pdf_a11y_contract')}
                onLoadError={handlePdfViewerError}
              />
            ) : (
              <div className="h-[60vh] flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground px-4 text-center">
                <p>{t('onboarding:contract_viewer_empty_unsigned')}</p>
              </div>
            )}
          </section>
        </div>

        {/* Ações / detalhes do contrato assinado (desativadas)
        {isSigned && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isDownloadingSigned}
              onClick={() => void downloadSignedSellerPdf('contract')}
            >
              {t('onboarding:download_contract')}
            </Button>
          </div>
        )}

        {isSigned && hasSignatureDetails && (
          <details className="rounded-lg border border-colorBorder bg-zinc-900/50 px-4 py-3 text-sm">
            <summary className="cursor-pointer text-zinc-300 font-medium">
              {t('onboarding:contract_signature_details')}
            </summary>
            <pre className="mt-3 max-h-48 overflow-auto rounded-md bg-zinc-950 p-3 text-xs text-zinc-400">
              {JSON.stringify(current?.signature, null, 2)}
            </pre>
          </details>
        )}
        */}

        <div className="flex items-start gap-3">
          <Checkbox
            id="agree"
            checked={agree}
            onCheckedChange={(v) => setAgree(Boolean(v))}
            disabled={acceptMutation.isPending || !version}
          />
          <div className="space-y-1 -mt-2">
            <Label htmlFor="agree">
              {t('onboarding:contract_agree_label')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('onboarding:contract_agree_hint')}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            disabled={!agree || acceptMutation.isPending || !version}
            onClick={() => acceptMutation.mutate()}
          >
            {acceptMutation.isPending
              ? t('onboarding:contract_submitting')
              : t('onboarding:contract_confirm_sign')}
          </Button>
        </div>

        {returnTo && (
          <p className="text-xs text-muted-foreground">
            {t('onboarding:contract_return_hint')}
          </p>
        )}
      </div>
    </LayoutInside>
  )
}